import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/Templates.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function Templates({ user }) {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ name: '', body: '' });
  const [editingId, setEditingId] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const roles = user?.roles || [];
  const canEdit = roles.includes('template_editor') || roles.includes('admin');

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/templates/`);
      setTemplates(res.data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.body.trim()) return;
    try {
      if (editingId) {
        await axios.put(`${API_BASE_URL}/templates/${editingId}/`, form);
        setStatusMsg('Template updated.');
      } else {
        await axios.post(`${API_BASE_URL}/templates/`, form);
        setStatusMsg('Template created.');
      }
      setStatusType('success');
      setForm({ name: '', body: '' });
      setEditingId(null);
      fetchTemplates();
    } catch (err) {
      const apiError = typeof err.response?.data === 'object' ? err.response?.data?.error : null;
      setStatusMsg('Error: ' + (apiError || (err.response?.status >= 500 ? 'Server error — please try again later.' : err.message)));
      setStatusType('error');
    }
  };

  const startEdit = (t) => {
    setForm({ name: t.name, body: t.body });
    setEditingId(t.id);
    setStatusMsg('');
  };

  const cancelEdit = () => {
    setForm({ name: '', body: '' });
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/templates/${id}/`);
      setConfirmDelete(null);
      setStatusMsg('Template deleted.');
      setStatusType('success');
      fetchTemplates();
    } catch (err) {
      setStatusMsg('Error deleting template.');
      setStatusType('error');
    }
  };

  if (!canEdit) return <div className="restricted">You do not have permission to access this page.</div>;

  return (
    <div className="templates-page">
      <h2>Templates</h2>

      {statusMsg && <div className={`status-msg status-${statusType}`}>{statusMsg}</div>}

      <div className="template-form-section">
        <h3>{editingId ? 'Edit Template' : 'New Template'}</h3>
        <form onSubmit={handleSubmit} className="template-form">
          <input
            type="text"
            placeholder="Template name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <textarea
            placeholder="Message body — supports {{name}}, {{firstname}}, {{lastname}}, {{date}}"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            rows={4}
            required
          />
          <p className="placeholder-hint">
            Available placeholders: <code>{'{{name}}'}</code> <code>{'{{firstname}}'}</code> <code>{'{{lastname}}'}</code> <code>{'{{date}}'}</code>
          </p>
          <div className="form-actions">
            <button type="submit" className="btn-save">{editingId ? 'Update' : 'Create'}</button>
            {editingId && <button type="button" className="btn-cancel" onClick={cancelEdit}>Cancel</button>}
          </div>
        </form>
      </div>

      <div className="template-list-section">
        <h3>Existing Templates</h3>
        {templates.length === 0 ? (
          <p className="no-data">No templates yet.</p>
        ) : (
          <ul className="template-list">
            {templates.map((t) => (
              <li key={t.id} className="template-item">
                <div className="template-info">
                  <strong className="template-name">{t.name}</strong>
                  <p className="template-body">{t.body}</p>
                </div>
                <div className="template-actions">
                  <button className="btn-edit" onClick={() => startEdit(t)}>Edit</button>
                  {confirmDelete === t.id ? (
                    <>
                      <span className="confirm-text">Delete?</span>
                      <button className="btn-confirm-delete" onClick={() => handleDelete(t.id)}>Yes</button>
                      <button className="btn-cancel" onClick={() => setConfirmDelete(null)}>No</button>
                    </>
                  ) : (
                    <button className="btn-delete" onClick={() => setConfirmDelete(t.id)}>Delete</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default Templates;
